import { useState } from "react";
import { Button } from "@nosible/shared/components/ui/button";
import { Input } from "@nosible/shared/components/ui/input";
import { Label } from "@nosible/shared/components/ui/label";
import { Textarea } from "@nosible/shared/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@nosible/shared/components/ui/dialog";
import { trpc } from "@/account/lib/trpc";
import { Key, Loader2, Check, X, Copy, Plus } from "lucide-react";

interface CreateApiKeyFormProps {
  subscriptionId: string;
  onSuccess: () => void;
}

export function CreateApiKeyForm({
  subscriptionId,
  onSuccess,
}: CreateApiKeyFormProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const createApiKey = trpc.subscription.apiKey.createApiKey.useMutation({
    onSuccess: (data) => {
      setCreatedKey(data.key);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    createApiKey.mutate({
      subscriptionId,
      name: name.trim(),
      description: description.trim() || undefined,
    });
  };

  const handleCopy = async () => {
    if (createdKey) {
      await navigator.clipboard.writeText(createdKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDone = () => {
    setCreatedKey(null);
    setName("");
    setDescription("");
    setOpen(false);
    onSuccess();
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && !createdKey) {
      setName("");
      setDescription("");
    }
    setOpen(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="account-outline-action relative z-50">
          <Plus className="h-4 w-4 mr-2" />
          New Key
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        {createdKey ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-neon-green">
                <Check className="h-5 w-5" />
                API Key Created
              </DialogTitle>
              <DialogDescription>
                Copy your API key now. You won't be able to see it again!
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-obsidian rounded-lg border border-slate-core">
                <code className="text-sm text-silica break-all font-mono">
                  {createdKey}
                </code>
              </div>
            </div>
            <DialogFooter className="flex gap-2 sm:justify-start">
              <Button onClick={handleCopy} variant="outline" className="flex-1">
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2 text-neon-green" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Key
                  </>
                )}
              </Button>
              <Button onClick={handleDone} className="brand-primary-button">Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="h-5 w-5 text-neon-green" />
                Create API Key
              </DialogTitle>
              <DialogDescription>
                Create a new API key for accessing the Nosible API
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="key-name">Name</Label>
                <Input
                  id="key-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My API Key"
                  required
                  maxLength={100}
                />
                <p className="text-xs text-chromium-mist">
                  A friendly name to identify this key
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="key-description">Description (optional)</Label>
                <Textarea
                  id="key-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What is this key used for?"
                  maxLength={500}
                  rows={2}
                />
              </div>

              {createApiKey.error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md">
                  <p className="text-sm text-red-400">{createApiKey.error.message}</p>
                </div>
              )}

              <DialogFooter className="pt-2">
                <Button
                  type="submit"
                  disabled={createApiKey.isPending || !name.trim()}
                  className="brand-primary-button w-full"
                >
                  {createApiKey.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Create Key
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
